import { useMemo } from 'react'
import type { AgentOutputProps } from '../types'

/**
 * TranscriptRenderer — used by meeting-transcriber.
 *
 * Expects { transcript_text, segments:[{ start, end, speaker, text }], speakers,
 * language, duration_seconds, word_count, stt_provider, stt_cost }.
 * Falls back to a plain-text view if segments are absent.
 */

interface Segment {
  start?: number
  end?: number
  speaker?: string | null
  text: string
}

interface Transcript {
  transcript_text?: string
  segments?: Segment[]
  speakers?: string[]
  language?: string
  duration_seconds?: number
  word_count?: number
  stt_provider?: string
  stt_cost?: number
}

function pickTranscript(result: any): Transcript | null {
  if (!result) return null
  const r = result
  const ad = r.agent_data || {}
  for (const src of [r, ad]) {
    if (typeof src?.transcript_text === 'string' || Array.isArray(src?.segments)) {
      return src as Transcript
    }
  }
  if (typeof r === 'string' && r.length > 0) return { transcript_text: r }
  return null
}

function fmtTs(s?: number): string {
  if (typeof s !== 'number') return ''
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

const SPEAKER_COLORS = ['#7C3AED', '#10B981', '#F59E0B', '#3B82F6', '#EC4899', '#14B8A6']

function colorFor(speaker: string, speakers: string[]): string {
  const i = speakers.indexOf(speaker)
  return SPEAKER_COLORS[(i >= 0 ? i : 0) % SPEAKER_COLORS.length]
}

export default function TranscriptRenderer(props: AgentOutputProps) {
  const t = useMemo(() => pickTranscript(props.result), [props.result])

  if (!t) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No transcript found in the agent result.
      </div>
    )
  }

  const speakers = t.speakers || Array.from(new Set((t.segments || []).map((s) => s.speaker || '').filter(Boolean)))
  const hasSegments = Array.isArray(t.segments) && t.segments.length > 0

  return (
    <div className="space-y-3">
      {/* Meta row */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {typeof t.duration_seconds === 'number' && <span>{fmtTs(t.duration_seconds)} total</span>}
        {typeof t.word_count === 'number' && <span>{t.word_count.toLocaleString()} words</span>}
        {t.language && <span>lang: {t.language}</span>}
        {speakers.length > 0 && <span>{speakers.length} speaker{speakers.length === 1 ? '' : 's'}</span>}
        {t.stt_provider && <span>via {t.stt_provider}</span>}
        {typeof t.stt_cost === 'number' && <span>${t.stt_cost.toFixed(4)}</span>}
      </div>

      {hasSegments ? (
        <div className="border border-border rounded-lg overflow-hidden max-h-[480px] overflow-y-auto">
          {t.segments!.map((seg, i) => {
            const color = seg.speaker ? colorFor(seg.speaker, speakers) : '#6B7280'
            return (
              <div
                key={i}
                className="flex gap-3 px-4 py-2 border-b border-border/60 last:border-0 hover:bg-muted/30"
              >
                <div className="text-xs font-mono text-muted-foreground w-14 shrink-0 pt-0.5">
                  {fmtTs(seg.start)}
                </div>
                {seg.speaker && (
                  <div
                    className="text-xs font-semibold w-24 shrink-0 truncate pt-0.5"
                    style={{ color }}
                    title={seg.speaker}
                  >
                    {seg.speaker}
                  </div>
                )}
                <div className="text-sm text-foreground flex-1 leading-relaxed">
                  {seg.text}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="border border-border rounded-lg p-4 bg-muted/20 max-h-[480px] overflow-y-auto">
          <pre className="text-sm text-foreground whitespace-pre-wrap font-sans leading-relaxed">
            {t.transcript_text || ''}
          </pre>
        </div>
      )}
    </div>
  )
}
